import type { DestinationMetadata, FilterOptions, QuickSearchData } from '@/scopes/search/quick-search/domain/types'
import type {
  FilterValue,
  QuickSearchApiFilter,
  QuickSearchApiResponse,
} from '@/scopes/search/quick-search/infrastructure/api/types'
import type { FilterOption } from '@/scopes/search/types'
import { createMetadataParser, MetadataParser } from '@/scopes/search/search/infrastructure/api/metadataParser'

interface Dependencies {
  metaDataParser?: MetadataParser
}

export function createQuickSearchResponseMapper(dependencies: Partial<Dependencies> = {}) {
  const { metaDataParser = createMetadataParser(),
  } = dependencies

  function mapFilterValue(filterValue: FilterValue) {
    return {
      name: filterValue.caption,
      value: filterValue.value,
      ...(filterValue.count && { resultsCount: filterValue.count }),
      ...(filterValue.groupInfo && { groupInfo: filterValue.groupInfo }),
      metadata: metaDataParser.parse(filterValue.metadata || ''),
    }
  }

  function mapFilterOptions(filters: QuickSearchApiFilter[]): Partial<FilterOptions> {
    return filters.reduce<Partial<FilterOptions>>((acc, filter) => {
      return {
        ...acc,
        [filter.type]:
          filter.type === 'Destination'
            ? mapDestinationValues(filter.values)
            : filter.values
                .filter(({ isAvailable }) => isAvailable)
                .map(value => mapFilterValue(value)),
      }
    }, {})
  }

  function map(response: QuickSearchApiResponse): QuickSearchData {
    return {
      searchLink: response.searchLink,
      searchLinkRelative: response.searchLinkRelative || '',
      totalResults: response.accommodationCount,
      filterOptions: mapFilterOptions(response.filters),
    }
  }

  return {
    map,
  }
}

function mapDestinationValues(values: QuickSearchApiFilter['values']): FilterOption<string, DestinationMetadata>[] {
  const filterValues = values
    .filter(({ isAvailable }) => isAvailable)
    .map(value => ({
      name: value.caption,
      value: value.value,
      ...(value.count && { resultsCount: value.count }),
      metadata: value.metadata ? parseDestinationMetadata(value.metadata) : ({} as DestinationMetadata),
    }))

  const countries: FilterOption<string, DestinationMetadata>[] = []
  const regionsByParentId = new Map<string, FilterOption<string, DestinationMetadata>[]>()
  const citiesByParentId = new Map<string, FilterOption<string, DestinationMetadata>[]>()

  filterValues.forEach((value) => {
    if (value.metadata.type === 'Country') {
      countries.push(value)
    }
    else if (value.metadata.type === 'Region' && value.metadata.parentId) {
      const regions = regionsByParentId.get(value.metadata.parentId) || []
      regions.push(value)
      regionsByParentId.set(value.metadata.parentId, regions)
    }
    else if (value.metadata.type === 'City' && value.metadata.parentId) {
      const cities = citiesByParentId.get(value.metadata.parentId) || []
      cities.push(value)
      citiesByParentId.set(value.metadata.parentId, cities)
    }
  })

  const orderedValues: FilterOption<string, DestinationMetadata>[] = []
  countries.forEach((country) => {
    orderedValues.push(country)

    const regions = regionsByParentId.get(country.value) || []
    regions.forEach((region) => {
      region.metadata.parentName = country.name
      orderedValues.push(region)

      const cities = citiesByParentId.get(region.value) || []
      cities.forEach((city) => {
        city.metadata.parentName = region.name
        orderedValues.push(city)
      })
    })
  })

  return orderedValues
}

function parseDestinationMetadata(metadata: string): DestinationMetadata {
  const parsedMetadata = Object.fromEntries(
    metadata.split(' || ').map(pair => pair.split(':').map(str => str.trim())),
  )

  return {
    type: parsedMetadata.Type,
    canonicalName: parsedMetadata.CanonicalName,
    ...(parsedMetadata.ParentId && { parentId: parsedMetadata.ParentId }),
  } as DestinationMetadata
}
